import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inventoryApi, ProductMovement } from '../../api/inventory.api';
import { devicesApi } from '../../api/devices.api';
import { productsApi, Product } from '../../api/products.api';
import { personnelApi, Personnel } from '../../api/personnel.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { serviceProvidersApi, ServiceProvider } from '../../api/service-providers.api';
import { repairFormsApi, CreateRepairFormDto } from '../../api/repair-forms.api';
import { scrapApprovalsApi } from '../../api/scrap-approvals.api';
import { generateRepairFormPDF } from '../../utils/pdfGenerator';
import { departmentsApi, DepartmentWithDeviceCount } from '../../api/departments.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import SearchableSelect, { SearchableSelectOption } from '../../components/forms/SearchableSelect';
import { useBranch } from '../../hooks/useBranch';

const InventoryList = () => {
  const { activeBranch } = useBranch();
  const [searchParams] = useSearchParams();
  const [movements, setMovements] = useState<ProductMovement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [departments, setDepartments] = useState<DepartmentWithDeviceCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRepairFormModalOpen, setIsRepairFormModalOpen] = useState(false);
  const [savedRepairFormId, setSavedRepairFormId] = useState<string | null>(null);
  const [showPrintOption, setShowPrintOption] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  
  // Initialize filters from URL parameters
  const initialFilters = () => {
    const serialNumber = searchParams.get('serialNumber') || '';
    const productId = searchParams.get('productId') || 'all';
    const type = searchParams.get('type') || 'all';
    
    return {
      serialNumber,
      branchId: 'all',
      productId,
      type,
      departmentId: 'all',
      personnelSearch: '',
    };
  };

  // Filter state
  const [filters, setFilters] = useState(initialFilters());
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    product: '',
    serialNumber: '',
    personnel: '',
    type: 'BranchTransfer',
    date: new Date().toISOString().split('T')[0],
    fromLocation: '',
    fromLocationId: '',
    toLocation: '',
    toLocationId: '',
    notes: '',
  });

  const [scrapPhotos, setScrapPhotos] = useState<File[]>([]);
  const [scrapPhotosPreviews, setScrapPhotosPreviews] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [repairFormData, setRepairFormData] = useState<CreateRepairFormDto>({
    device: '',
    serialNumber: '',
    assetTag: '',
    serviceProvider: '',
    serviceProviderName: '',
    faultDescription: '',
    accessories: '',
    deviceCondition: 'Good',
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveredBy: '',
    notes: '',
  });

  useEffect(() => {
    fetchMovements();
  }, [currentPage, filters]);

  // Read URL parameters for department (needs lookup)
  useEffect(() => {
    const department = searchParams.get('department');

    if (department && departments.length > 0) {
      const foundDepartment = departments.find(d => d.name === decodeURIComponent(department));
      if (foundDepartment && foundDepartment.id !== filters.departmentId) {
        setFilters(prev => ({ ...prev, departmentId: foundDepartment.id }));
      }
    }
  }, [searchParams, departments]);

  useEffect(() => {
    fetchProducts();
    fetchPersonnel();
    fetchBranches();
    fetchServiceProviders();
    fetchDepartments();
  }, []);

  const fetchMovements = async () => {
    try {
      setIsLoading(true);
      const response = await inventoryApi.getAllMovements({
        serialNumber: filters.serialNumber || undefined,
        branchId: filters.branchId !== 'all' ? filters.branchId : undefined,
        productId: filters.productId !== 'all' ? filters.productId : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        departmentId: filters.departmentId !== 'all' ? filters.departmentId : undefined,
        personnelSearch: filters.personnelSearch || undefined,
        page: currentPage,
        limit,
      });
      setMovements(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hareketler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await productsApi.getAll();
      setProducts(data.filter((p) => p.isActive));
    } catch (err: any) {
      console.error('Ürünler yüklenirken hata:', err);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const data = await personnelApi.getAll();
      setPersonnel(data.filter((p) => p.isActive));
    } catch (err: any) {
      console.error('Personel yüklenirken hata:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await branchesApi.getAll();
      setBranches(data.filter((b) => b.isActive));
    } catch (err: any) {
      console.error('Şubeler yüklenirken hata:', err);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const data = await serviceProvidersApi.getAll();
      setServiceProviders(data.filter((s) => s.isActive));
    } catch (err: any) {
      console.error('Servisler yüklenirken hata:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentsApi.getAllWithDeviceCounts();
      setDepartments(data.filter((d) => d.isActive));
    } catch (err: any) {
      console.error('Departmanlar yüklenirken hata:', err);
    }
  };

  const handleDeviceChange = (deviceId: string) => {
    // Device bilgilerini backend'den çek
    devicesApi.getOne(deviceId).then((device) => {
      setFormData({
        ...formData,
        product: deviceId,
        serialNumber: device.serialNumber || '',
        fromLocation: device.currentLocation?.name || '',
        fromLocationId: device.currentLocation?.id || '',
      });
    }).catch((err) => {
      console.error('Cihaz bilgileri alınamadı:', err);
      setError('Cihaz bilgileri alınamadı');
    });
  };

  const handleDeviceSearch = async (query: string): Promise<SearchableSelectOption[]> => {
    try {
      // Backend'den search yap
      const devices = await devicesApi.search(query);
      
      // Sadece aktif cihazları döndür
      const activeDevices = devices.filter((d) => d.isActive);
      
      return activeDevices.map((device) => ({
        value: device.id,
        label: `${device.name} - ${device.serialNumber} ${device.brand ? `(${device.brand})` : ''}`,
      }));
    } catch (err) {
      console.error('Cihaz arama hatası:', err);
      return [];
    }
  };

  const handleTypeChange = (type: string) => {
    setFormData({
      ...formData,
      type,
      personnel: '',
      toLocation: type === 'Scrap' ? 'Hurda' : '',
      toLocationId: '',
    });
    // Clear photos when changing type
    if (type !== 'Scrap') {
      setScrapPhotos([]);
      setScrapPhotosPreviews([]);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    setScrapPhotos((prev) => [...prev, ...newFiles]);

    // Create previews
    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setScrapPhotosPreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = (index: number) => {
    setScrapPhotos((prev) => prev.filter((_, i) => i !== index));
    setScrapPhotosPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenModal = () => {
    setFormData({
      product: '',
      serialNumber: '',
      personnel: '',
      type: 'BranchTransfer',
      date: new Date().toISOString().split('T')[0],
      fromLocation: '',
      fromLocationId: '',
      toLocation: '',
      toLocationId: '',
      notes: '',
    });
    setScrapPhotos([]);
    setScrapPhotosPreviews([]);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseRepairFormModal = () => {
    setIsRepairFormModalOpen(false);
  };

  const handleRepairFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!repairFormData.faultDescription) {
      setError('Arıza açıklaması zorunludur');
      return;
    }

    if (!repairFormData.deliveredBy) {
      setError('Teslim eden kişi zorunludur');
      return;
    }

    try {
      // Create repair form
      const savedForm = await repairFormsApi.create(repairFormData);
      setSavedRepairFormId(savedForm.id);
      
      // Create movement
      const cleanedData = {
        ...formData,
        toLocationId: formData.toLocationId || undefined,
        fromLocationId: formData.fromLocationId || undefined,
        personnel: formData.personnel || undefined,
      };
      
      await inventoryApi.createMovement(cleanedData);
      await fetchMovements();
      
      // Show print option
      setShowPrintOption(true);
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handlePrintRepairForm = async () => {
    if (!savedRepairFormId) return;

    try {
      // Get full form data with populated fields
      const form = await repairFormsApi.getOne(savedRepairFormId);
      
      // Extract device ID (handle both string and object)
      const deviceId = typeof form.device === 'string' 
        ? form.device 
        : (form.device as any)?._id || (form.device as any)?.id;
      
      if (!deviceId) {
        throw new Error('Cihaz ID bulunamadı');
      }
      
      const device = await devicesApi.getOne(deviceId);
      
      // Extract service provider ID
      const serviceId = typeof form.serviceProvider === 'string'
        ? form.serviceProvider
        : (form.serviceProvider as any)?._id || (form.serviceProvider as any)?.id;
      
      const service = serviceProviders.find(s => s.id === serviceId);
      
      // Get product name, brand, and model
      let productName = 'Bilinmiyor';
      let brand = '';
      let model = '';
      
      console.log('Device:', device);
      console.log('Device Product:', device.product);
      console.log('Device Brand:', device.brand);
      
      if (typeof device.product === 'object' && device.product !== null) {
        productName = (device.product as any).name || 'Bilinmiyor';
        console.log('Product Name:', productName);
        
        // Extract brand from product
        if ((device.product as any).brand) {
          const productBrand = (device.product as any).brand;
          console.log('Product Brand Object:', productBrand);
          
          if (typeof productBrand === 'object' && productBrand !== null) {
            brand = productBrand.name || productBrand._id || String(productBrand);
          } else {
            brand = String(productBrand);
          }
          console.log('Extracted Brand:', brand);
        }
        
        // Extract model from product
        if ((device.product as any).model) {
          const productModel = (device.product as any).model;
          if (typeof productModel === 'object' && productModel !== null) {
            model = productModel.name || String(productModel);
          } else {
            model = String(productModel);
          }
        }
      }
      
      // Fallback to device-level brand and model
      if (!brand && device.brand) {
        if (typeof device.brand === 'object' && device.brand !== null) {
          brand = (device.brand as any).name || String(device.brand);
        } else {
          brand = String(device.brand);
        }
      }
      
      if (!model && device.model) {
        if (typeof device.model === 'object' && device.model !== null) {
          model = (device.model as any).name || String(device.model);
        } else {
          model = String(device.model);
        }
      }

      console.log('Final Brand:', brand);
      console.log('Final Model:', model);

      // Generate PDF
      generateRepairFormPDF({
        formNumber: form.id.substring(0, 8).toUpperCase(),
        date: new Date(form.createdAt).toLocaleDateString('tr-TR'),
        deviceInfo: {
          serialNumber: form.serialNumber,
          assetTag: form.assetTag || 'Belirtilmemis',
          productName: productName,
          brand: brand || 'Bilinmiyor',
          model: model || 'Bilinmiyor',
          condition: form.deviceCondition || 'Good',
        },
        serviceProvider: {
          name: form.serviceProviderName,
          contactPerson: service?.contactPerson,
          phone: service?.phone,
          address: service?.address,
        },
        repairDetails: {
          faultDescription: form.faultDescription,
          accessories: form.accessories,
        },
        delivery: {
          deliveryDate: form.deliveryDate,
          deliveredBy: form.deliveredBy,
          receivedBy: form.receivedBy,
        },
        branch: {
          name: activeBranch?.name || 'Şube',
          address: activeBranch?.address,
          phone: activeBranch?.phone,
        },
        notes: form.notes,
      });

      // Close modals and reset
      handleCloseModal();
      handleCloseRepairFormModal();
      setShowPrintOption(false);
      setSavedRepairFormId(null);
    } catch (err: any) {
      setError(err.message || 'PDF oluşturulamadı');
    }
  };

  const handleSkipPrint = () => {
    handleCloseModal();
    handleCloseRepairFormModal();
    setShowPrintOption(false);
    setSavedRepairFormId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.product) {
      setError('Cihaz seçimi zorunludur');
      return;
    }

    // If repair type, open repair form modal instead
    if (formData.type === 'Repair') {
      // Get device details for repair form
      try {
        const device = await devicesApi.getOne(formData.product);
        const service = serviceProviders.find(s => s.id === formData.toLocationId);
        
        setRepairFormData({
          device: formData.product,
          serialNumber: formData.serialNumber,
          assetTag: device.assetTag || 'Belirtilmemiş',
          serviceProvider: formData.toLocationId,
          serviceProviderName: service?.name || formData.toLocation,
          faultDescription: '',
          accessories: '',
          deviceCondition: device.condition || 'Good',
          deliveryDate: new Date().toISOString().split('T')[0],
          deliveredBy: '',
          notes: formData.notes || '',
        });
        
        setIsRepairFormModalOpen(true);
        return;
      } catch (err: any) {
        setError(err.message || 'Cihaz bilgileri alınamadı');
        return;
      }
    }

    // If scrap type, create approval request instead of direct movement
    if (formData.type === 'Scrap') {
      try {
        setUploadingPhoto(true);
        
        // Upload photos to MinIO first
        let photoUrls: string[] = [];
        if (scrapPhotos.length > 0) {
          photoUrls = await scrapApprovalsApi.uploadPhotos(scrapPhotos);
        }
        
        await scrapApprovalsApi.create({
          device: formData.product,
          serialNumber: formData.serialNumber,
          reason: formData.notes || 'Hurda talebi',
          notes: formData.notes,
          photos: photoUrls,
        });
        
        setUploadingPhoto(false);
        alert('Hurda talebi oluşturuldu. Admin onayı bekleniyor.');
        handleCloseModal();
        setScrapPhotos([]);
        setScrapPhotosPreviews([]);
        return;
      } catch (err: any) {
        setUploadingPhoto(false);
        setError(err.message || 'Hurda talebi oluşturulamadı');
        return;
      }
    }

    if (!formData.toLocation) {
      setError('Hedef konum zorunludur');
      return;
    }

    try {
      // Clean up empty strings to undefined for optional fields
      const cleanedData = {
        ...formData,
        toLocationId: formData.toLocationId || undefined,
        fromLocationId: formData.fromLocationId || undefined,
        personnel: formData.personnel || undefined,
      };
      
      await inventoryApi.createMovement(cleanedData);
      await fetchMovements();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu hareketi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await inventoryApi.deleteMovement(id);
      await fetchMovements();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const getDeviceName = (device: any): string => {
    if (typeof device === 'object' && device !== null) {
      return `${device.name} (${device.serialNumber})`;
    }
    return '-';
  };

  const getPersonnelName = (personnel: any): string => {
    if (typeof personnel === 'object' && personnel !== null) {
      return `${personnel.firstName} ${personnel.lastName}`;
    }
    return '-';
  };

  const getCreatedByName = (createdBy: any): string => {
    if (typeof createdBy === 'object' && createdBy !== null) {
      if (createdBy.firstName && createdBy.lastName) {
        return `${createdBy.firstName} ${createdBy.lastName}`;
      }
      return createdBy.email || createdBy.username || '-';
    }
    return '-';
  };

  const getTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      Assignment: 'bg-blue-100 text-blue-800',
      Return: 'bg-green-100 text-green-800',
      Transfer: 'bg-purple-100 text-purple-800',
      Repair: 'bg-yellow-100 text-yellow-800',
      BranchTransfer: 'bg-indigo-100 text-indigo-800',
      Scrap: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      Assignment: 'Zimmet',
      Return: 'İade',
      Transfer: 'Transfer',
      Repair: 'Onarım',
      BranchTransfer: 'Şube Transferi',
      Scrap: 'Hurda',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[type] || 'bg-gray-100 text-gray-800'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const columns: Column<ProductMovement>[] = [
    { 
      key: 'date', 
      header: 'Tarih', 
      render: (item) => {
        const date = new Date(item.date);
        return date.toLocaleDateString('tr-TR');
      }
    },
    { key: 'serialNumber', header: 'Seri No' },
    { 
      key: 'product', 
      header: 'Cihaz', 
      render: (item) => {
        const deviceName = getDeviceName(item.product);
        return deviceName;
      }
    },
    { key: 'type', header: 'Hareket Tipi', render: (item) => getTypeBadge(item.type) },
    { key: 'fromLocation', header: 'Nereden', render: (item) => item.fromLocation || '-' },
    { key: 'toLocation', header: 'Nereye' },
    { key: 'personnel', header: 'Personel', render: (item) => item.personnel ? getPersonnelName(item.personnel) : '-' },
    { key: 'createdBy', header: 'İşlemi Yapan', render: (item) => getCreatedByName(item.createdBy) },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
          Sil
        </Button>
      ),
    },
  ];

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setFilters({
      serialNumber: '',
      branchId: 'all',
      productId: 'all',
      type: 'all',
      departmentId: 'all',
      personnelSearch: '',
    });
    setCurrentPage(1);
  };

  if (isLoading) {
    return <Loading text="Hareketler yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cihaz Hareketleri</h1>
        <Button onClick={handleOpenModal}>Yeni Hareket Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="mb-4 flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={() => setShowFilters(!showFilters)}
          size="sm"
        >
          {showFilters ? '🔼 Filtreleri Gizle' : '🔽 Filtreleri Göster'}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Filtreler</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="mb-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seri No
              </label>
              <input
                type="text"
                value={filters.serialNumber}
                onChange={(e) => handleFilterChange('serialNumber', e.target.value)}
                placeholder="Seri numarası ile ara..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Select
              label="Şube"
              value={filters.branchId}
              onChange={(value) => handleFilterChange('branchId', value)}
              options={[
                { value: 'all', label: 'Tüm Şubeler' },
                ...branches.map((b) => ({ value: b.id, label: b.name }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Ürün"
              value={filters.productId}
              onChange={(value) => handleFilterChange('productId', value)}
              options={[
                { value: 'all', label: 'Tüm Ürünler' },
                ...products.map((p) => {
                  const brandName = typeof p.brand === 'object' && p.brand !== null 
                    ? (p.brand as any).name 
                    : p.brand || '';
                  return {
                    value: p.id,
                    label: `${p.name} ${brandName ? `- ${brandName}` : ''}`
                  };
                })
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Hareket Tipi"
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              options={[
                { value: 'all', label: 'Tüm Tipler' },
                { value: 'BranchTransfer', label: 'Şube Transferi' },
                { value: 'Assignment', label: 'Zimmet' },
                { value: 'Return', label: 'İade' },
                { value: 'Repair', label: 'Onarım' },
                { value: 'Transfer', label: 'Transfer' },
                { value: 'Scrap', label: 'Hurda' }
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Departman"
              value={filters.departmentId}
              onChange={(value) => handleFilterChange('departmentId', value)}
              options={[
                { value: 'all', label: 'Tüm Departmanlar' },
                ...departments.map((d) => ({ 
                  value: d.id, 
                  label: d.name
                }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <div className="mb-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personel Ara
              </label>
              <input
                type="text"
                value={filters.personnelSearch}
                onChange={(e) => handleFilterChange('personnelSearch', e.target.value)}
                placeholder="Ad, soyad veya numara..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={handleClearFilters}>
              Temizle
            </Button>
          </div>
        </div>
      )}

      {/* Results info */}
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Toplam <span className="font-semibold">{total}</span> kayıt bulundu
        </div>
        <div className="text-sm text-gray-600">
          Sayfa <span className="font-semibold">{currentPage}</span> / <span className="font-semibold">{totalPages}</span>
        </div>
      </div>

      <Table data={movements} columns={columns} emptyMessage="Henüz hareket bulunmamaktadır" />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">
            Gösterilen: {(currentPage - 1) * limit + 1} - {Math.min(currentPage * limit, total)} / {total}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Önceki
            </Button>
            
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="min-w-[40px]"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Sonraki →
            </Button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Yeni Hareket Ekle"
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <SearchableSelect
            label="Cihaz"
            value={formData.product}
            onChange={(value) => handleDeviceChange(value)}
            onSearch={handleDeviceSearch}
            placeholder="Cihaz adı veya seri numarası ile arayın..."
            required
            minSearchLength={2}
            helperText="En az 2 karakter girerek arama yapabilirsiniz"
          />

          {formData.serialNumber && (
            <Input
              label="Seri Numarası"
              value={formData.serialNumber}
              disabled
            />
          )}

          <Select
            label="Hareket Tipi"
            value={formData.type}
            onChange={(value) => handleTypeChange(value)}
            options={[
              { value: 'BranchTransfer', label: 'Şube Transferi' },
              { value: 'Assignment', label: 'Personele Zimmet' },
              { value: 'Return', label: 'İade' },
              { value: 'Repair', label: 'Onarıma Gönder' },
              { value: 'Transfer', label: 'Diğer Transfer' },
              { value: 'Scrap', label: 'Hurda' },
            ]}
            searchable={true}
            required
          />

          {formData.fromLocation && (
            <Input
              label="Mevcut Konum"
              value={formData.fromLocation}
              disabled
            />
          )}

          {formData.type === 'BranchTransfer' && (
            <Select
              label="Hedef Şube"
              value={formData.toLocationId}
              onChange={(value) => {
                const branch = branches.find((b) => b.id === value);
                setFormData({
                  ...formData,
                  toLocationId: value,
                  toLocation: branch?.name || '',
                });
              }}
              options={branches.map((b) => ({ value: b.id, label: b.name }))}
              placeholder="Şube seçiniz"
              searchable={true}
              required
            />
          )}

          {formData.type === 'Assignment' && (
            <Select
              label="Personel"
              value={formData.personnel}
              onChange={(value) => {
                const person = personnel.find((p) => p.id === value);
                setFormData({
                  ...formData,
                  personnel: value,
                  toLocationId: value,
                  toLocation: person ? `${person.firstName} ${person.lastName}` : '',
                });
              }}
              options={personnel.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }))}
              placeholder="Personel seçiniz"
              searchable={true}
              required
            />
          )}

          {formData.type === 'Repair' && (
            <Select
              label="Servis"
              value={formData.toLocationId}
              onChange={(value) => {
                const service = serviceProviders.find((s) => s.id === value);
                setFormData({
                  ...formData,
                  toLocationId: value,
                  toLocation: service?.name || '',
                });
              }}
              options={serviceProviders.map((s) => ({ value: s.id, label: s.name }))}
              placeholder="Servis seçiniz"
              searchable={true}
              required
            />
          )}

          {(formData.type === 'Return' || formData.type === 'Transfer') && (
            <Input
              label="Hedef Konum"
              value={formData.toLocation}
              onChange={(e) => setFormData({ ...formData, toLocation: e.target.value })}
              placeholder="Örn: Depo, Merkez, vb."
              required
            />
          )}

          {formData.type === 'Scrap' && (
            <>
              <Input
                label="Hedef Konum"
                value="Hurda"
                disabled
                helperText="Cihaz hurda onayına gönderilecek. Admin onayından sonra stoktan düşecek."
              />
              <Input
                label="Hurda Sebebi"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Neden hurdaya gönderiliyor?"
                helperText="Hurda sebebini belirtmeniz önerilir"
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fotoğraflar
                </label>
                <div className="mb-3">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Cihazın fotoğraflarını yükleyin (birden fazla seçebilirsiniz, max 10)
                  </p>
                </div>
                
                {scrapPhotosPreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {scrapPhotosPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Hurda fotoğraf ${index + 1}`}
                          className="w-full h-24 object-cover rounded-md border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Input
            label="Tarih"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />

          <Input
            label="Notlar"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Opsiyonel notlar"
          />

          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth disabled={uploadingPhoto}>
              {uploadingPhoto ? 'Fotoğraflar yükleniyor...' : formData.type === 'Repair' ? 'Servis Formu Doldur' : formData.type === 'Scrap' ? 'Hurda Onayı Talep Et' : 'Kaydet'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal} disabled={uploadingPhoto}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      {/* Repair Form Modal */}
      <Modal
        isOpen={isRepairFormModalOpen}
        onClose={handleCloseRepairFormModal}
        title="Servis Teslim Formu"
        size="lg"
      >
        <form onSubmit={handleRepairFormSubmit}>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm font-semibold text-blue-900 mb-1">📋 Servis Teslim Formu</p>
            <p className="text-xs text-blue-700">
              Cihazı servise teslim etmeden önce aşağıdaki bilgileri doldurun
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input
              label="Seri No"
              value={repairFormData.serialNumber}
              disabled
            />
            <Input
              label="Varlık Etiketi"
              value={repairFormData.assetTag}
              onChange={(e) => setRepairFormData({ ...repairFormData, assetTag: e.target.value })}
              placeholder="Varlık etiketi giriniz (opsiyonel)"
            />
          </div>

          <Input
            label="Servis"
            value={repairFormData.serviceProviderName}
            disabled
          />

          <Input
            label="Arıza Açıklaması"
            value={repairFormData.faultDescription}
            onChange={(e) => setRepairFormData({ ...repairFormData, faultDescription: e.target.value })}
            placeholder="Cihazın arızasını detaylı açıklayın..."
            required
          />

          <Input
            label="Aksesuarlar"
            value={repairFormData.accessories}
            onChange={(e) => setRepairFormData({ ...repairFormData, accessories: e.target.value })}
            placeholder="Örn: Şarj aleti, kılıf, kulaklık..."
          />

          <Select
            label="Cihaz Durumu"
            value={repairFormData.deviceCondition}
            onChange={(value) => setRepairFormData({ ...repairFormData, deviceCondition: value })}
            options={[
              { value: 'New', label: 'Yeni' },
              { value: 'Good', label: 'İyi' },
              { value: 'Fair', label: 'Orta' },
              { value: 'Poor', label: 'Kötü' },
              { value: 'Broken', label: 'Bozuk' },
            ]}
            searchable={true}
          />

          <Input
            label="Teslim Tarihi"
            type="date"
            value={repairFormData.deliveryDate}
            onChange={(e) => setRepairFormData({ ...repairFormData, deliveryDate: e.target.value })}
            required
          />

          <Input
            label="Teslim Eden Kişi"
            value={repairFormData.deliveredBy}
            onChange={(e) => setRepairFormData({ ...repairFormData, deliveredBy: e.target.value })}
            placeholder="Ad Soyad"
            required
          />

          <Input
            label="Notlar"
            value={repairFormData.notes}
            onChange={(e) => setRepairFormData({ ...repairFormData, notes: e.target.value })}
            placeholder="Ek notlar..."
          />

          {!showPrintOption ? (
            <div className="flex gap-2 mt-6">
              <Button type="submit" fullWidth>
                Kaydet ve Onarıma Gönder
              </Button>
              <Button type="button" variant="outline" fullWidth onClick={handleCloseRepairFormModal}>
                İptal
              </Button>
            </div>
          ) : (
            <div className="mt-6">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm font-semibold text-green-900 mb-1">✅ Form Başarıyla Kaydedildi</p>
                <p className="text-xs text-green-700">
                  Servis teslim formu oluşturuldu. Yazdırmak ister misiniz?
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={handlePrintRepairForm} fullWidth>
                  🖨️ Formu Yazdır
                </Button>
                <Button type="button" variant="outline" fullWidth onClick={handleSkipPrint}>
                  Atla
                </Button>
              </div>
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
};


export default InventoryList;
